import { PxtraApiClientLegacy } from '@pxtra-client-lib-legacy';
import { ExportRequest, ExportTask, ImportTask } from '@pxtra-client-lib-legacy/gen';
import { makeAutoObservable, runInAction, when } from 'mobx';
import Util from '../misc/util';
import AuthenticationStore from './authenticationStore';
import CompanyStore from './companyStore';
import { SupportedExportTypes } from '../pages/exports/store/exportFlowStore';

class ImpexStore {
  private apiClient: PxtraApiClientLegacy;

  private _exportRequest: Partial<ExportRequest> | null = null;
  private _exportList: Map<string, ExportTask>;
  private _importList: Map<string, ImportTask>;
  private _currentExportId?: string;
  private _currentImportId?: string;

  public constructor(
    apiClient: PxtraApiClientLegacy,
    private companyStore: CompanyStore,
    private authenticationStore: AuthenticationStore
  ) {
    this.apiClient = apiClient;
    this._exportList = new Map<string, ExportTask>();
    this._importList = new Map<string, ImportTask>();
    makeAutoObservable(this);
    when(
      () => !this.authenticationStore.isLoggedIn,
      () => {
        this._clear();
      }
    );
  }

  private _clear() {
    this._exportList = new Map<string, ExportTask>();
    this._importList = new Map<string, ImportTask>();
    this._currentExportId = undefined;
    this._currentImportId = undefined;
  }

  public get exportRequest() {
    return this._exportRequest;
  }

  public set exportRequest(request: Partial<ExportRequest> | null) {
    this._exportRequest = request;
  }

  public get exportList() {
    return this.getList(this._exportList);
  }

  public get ImportList() {
    return this.getList(this._importList);
  }

  public get currentExport() {
    return this._currentExportId ? this._exportList.get(this._currentExportId) : undefined;
  }

  public get currentImport() {
    return this._currentImportId ? this._importList.get(this._currentImportId) : undefined;
  }

  public set currentImportId(id: string) {
    this._currentImportId = id;
  }

  public initializeExportRequest(type: SupportedExportTypes) {
    // Initialize a new export request based on type
    this._exportRequest = {
      export_type: type as 'employee_data' | 'payroll_data' | 'receipts',
      company: this.companyStore.company?.id
    };
  }

  public updateExportRequest(updates: Partial<ExportRequest>) {
    if (!this._exportRequest) return;

    this._exportRequest = {
      ...this._exportRequest,
      ...updates
    } as Partial<ExportRequest>;
  }

  public async fetchExportList(startDate?: Date, endDate?: Date) {
    if (!this.companyStore.company) throw Error('No company selected');

    const createdAtAfter = startDate;
    const createdAtBefore = endDate ? endDate : Util.now;
    const response = await this.apiClient.impex.impexExportList({
      company: this.companyStore.company.id,
      createdAtAfter: !createdAtAfter ? undefined : Util.getDateString(createdAtAfter),
      createdAtBefore: Util.getDateString(createdAtBefore)
    });
    runInAction(() => (this._exportList = new Map(response?.results?.map((r) => [r.id, r]))));
  }

  public async fetchImportList(startDate?: Date, endDate?: Date) {
    if (!this.companyStore.company) throw Error('No company selected');

    const createdAtAfter = startDate;
    const createdAtBefore = endDate ? endDate : Util.now;
    const response = await this.apiClient.impex.impexImportList({
      company: this.companyStore.company.id,
      createdAtAfter: !createdAtAfter ? undefined : Util.getDateString(createdAtAfter),
      createdAtBefore: Util.getDateString(createdAtBefore)
    });
    runInAction(() => (this._importList = new Map(response?.results?.map((r) => [r.id, r]))));
  }

  public async fetchImportEmployeeList() {
    if (!this.companyStore.company) throw Error('No company selected');

    const response = await this.apiClient.impex.impexImportList({
      company: this.companyStore.company.id
    });
    runInAction(() => (this._importList = new Map(response?.results?.map((r) => [r.id, r]))));
  }

  public async createExport(request: ExportRequest) {
    if (!this.companyStore.company) throw Error('No company selected');

    const result = await this.apiClient.impex.impexExportCreate({
      requestBody: {
        ...request,
        company: this.companyStore.company?.id
      }
    });
    this._currentExportId = result.id;
    return result.id;
  }

  public async deleteExport(id: string) {
    if (!this.companyStore.company) throw Error('No company selected');
    await this.apiClient.impex.impexExportDestroy({ id });
  }

  //NOT WORKING :/
  private updateMap<T extends { id: string }>(map: Map<string, T>, items: T[]) {
    runInAction(() => (map = new Map(items.map((r) => [r.id, r]))));
  }

  private getList<T>(map: Map<string, T>) {
    return map ? Array.from(map.values()) : new Array<T>();
  }
}

export default ImpexStore;
